/* eslint-disable react/forbid-prop-types */
import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import moment from 'moment';
import { useMutation } from 'react-query';
import { Link } from 'react-router-dom';
import ModalBodyLayout from '../../../shared/ModalBodyLayout';
import { MODAL_SCREENS } from '../../constants';
import { formatNumber, formatNumberWithComma } from '../../../../utilities/formatHelpers';
import { currencies, exchange } from '../../../../utilities/constants';
import { YIELD_OFFER_ACTIVATE, YIELD_OFFER_DETAILS } from '../../mutations';
import { showAlert } from '../../../../utilities/notifications';
import { GenericLoading } from '../../../../components/Placeholders';
import { useSetting } from '../../../../utilities/sharedHooks/settings';

const ConfirmDeposit = ({
  openScreen,
  offerDetails,
  refetchDeposited,
  setConfirmedOffer,
  refrechWallets,
  hide,
  selectedYieldType,
}) => {
  const { t: translate } = useTranslation();

  const [details, setDetails] = useState({});
  const [timer, setTimer] = useState(0);

  const [duration, setDuration] = useState(0);

  const [offerError, setOfferError] = useState(false);
  const [termsCheckbox, setTermsCheckbox] = useState(false);
  const [riskCheckbox, setRiskCheckbox] = useState(false);

  const [oxygenYieldTermsUrl] = useSetting('content_urls', 'content_url_oxygen_yield_terms');

  const { mutate: activateOffer, isLoading } = useMutation(YIELD_OFFER_ACTIVATE, {
    onSettled(_, error) {
      if (!error) {
        setOfferError(false);
        refetchDeposited();
        refrechWallets();
        showAlert({ message: translate('deposit_success_msg'), type: 'success' });
        hide();
      } else if (error?.status === 409) {
        setOfferError(true);
      } else {
        showAlert({ message: error.message, type: 'danger' });
        hide();
      }
    },
  });

  const { mutate: getOfferDetails, isLoading: isLoadingOfferDetails } = useMutation(
    YIELD_OFFER_DETAILS,
    {
      onSettled(data, error) {
        if (!error) {
          setOfferError(false);
          setDetails(data);
          const currentDate = moment();
          const effectiveDate = moment(data?.contractAttributes?.effectiveDate);
          const effectiveTime = effectiveDate.diff(currentDate, 'seconds');
          setTimer(effectiveTime);

          const expiryDate = moment(data?.product?.expirationDate);
          const days = expiryDate.diff(currentDate, 'days');

          setDuration(days);

          setConfirmedOffer({
            annualizedYield: data?.contractAttributes?.annualizedYieldPct,
            amount: data?.contractAttributes?.amount,
            linkedPrice: data?.product?.linkedPrice,
            expirationDate: data?.product?.expirationDate,
            id: data?.contractId,
          });
        } else {
          setOfferError(true);
        }
      },
    }
  );

  const timerRef = useRef(null);

  useEffect(() => {
    timerRef.current = setTimeout(() => {
      if (timer > 0) setTimer(timer - 1);
    }, 1000);

    return () => {
      clearTimeout(timerRef.current);
    };
  }, [offerDetails, timer]);

  useEffect(() => {
    if (details && details?.product && timer === 0 && !offerError && !isLoadingOfferDetails) {
      getOfferDetails({
        productId: details?.product?.id,
        amount: details?.contractAttributes?.amount,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timer, isLoadingOfferDetails, offerError]);

  const handleConfirm = () => {
    activateOffer({ contractId: details?.contractId });
  };

  useEffect(() => {
    if (offerDetails && offerDetails?.productId) {
      getOfferDetails({ productId: offerDetails?.productId, amount: offerDetails?.amount });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const invistDisabled = useMemo(
    () => !termsCheckbox || !riskCheckbox || isLoading || isLoadingOfferDetails || timer === 0,
    [termsCheckbox, isLoading, isLoadingOfferDetails, timer, riskCheckbox]
  );

  const handleTryAgain = () => {
    setOfferError(false);
    clearTimeout(timerRef.current);

    if (offerDetails && offerDetails?.productId) {
      getOfferDetails({ productId: offerDetails?.productId, amount: offerDetails?.amount });
    } else {
      getOfferDetails({
        productId: details?.product?.id,
        amount: details?.contractAttributes?.amount,
      });
    }
  };

  const renderFooter = () =>
    !offerError ? (
      <>
        <p className="text-shadow text-f6">{translate('confirm_deposit_note_label')}</p>
        <div className="form-check ps-1 mb-2 mt-4 ">
          <label
            className="form-check-label d-flex flex-row justify-content-start align-items-center"
            style={{ height: '1rem' }}>
            <input
              type="checkbox"
              className={`form-check-input ms-2 ${!termsCheckbox ? 'bg-light' : ''}`}
              checked={termsCheckbox}
              onChange={() => setTermsCheckbox((prev) => !prev)}
              required
            />
            <p className="ps-2 text-f6 w-100">
              {translate('yield_terms_label_part_1')}{' '}
              <a
                target="_blank"
                className="link text-decoration-none"
                href={oxygenYieldTermsUrl || '#'}
                download={translate('yield_terms_label_part_2')}
                rel="noreferrer">
                {translate('yield_terms_label_part_2')}
              </a>
            </p>
          </label>
        </div>
        <div className="form-check ps-1 mb-2 mt-2 ">
          <label
            className="form-check-label d-flex flex-row justify-content-start align-items-center"
            style={{ height: '1rem' }}>
            <input
              type="checkbox"
              className={`form-check-input ms-2 ${!riskCheckbox ? 'bg-light' : ''}`}
              checked={riskCheckbox}
              onChange={() => setRiskCheckbox((prev) => !prev)}
              required
            />
            <p className="ps-2 text-f6 w-100">
              {translate('yield_risks_label_part_1')}
              <Link to={`${selectedYieldType}/faq`} className="link text-decoration-none">
                {` ${translate('yield_risks_label_part_2')} `}
              </Link>
              {translate('yield_risks_label_part_3')}
            </p>
          </label>
        </div>
        <div className="d-flex flex-row justify-content-center mb-4">
          <p className="text-f6 text-shadow">
            {translate('yield_qoute_label').replace('__TIMER__', timer)}
          </p>
        </div>
        <button
          className="btn btn-primary"
          loading={isLoading ? 'true' : 'false'}
          type="button"
          onClick={handleConfirm}
          disabled={invistDisabled}>
          {translate('confirm_deposit_btn_with_timer_label')}
        </button>
      </>
    ) : (
      <button className="btn btn-primary" type="button" onClick={handleTryAgain}>
        {translate('try_again_btn_title')}
      </button>
    );

  return (
    <ModalBodyLayout
      withBack
      onPrevClick={() => openScreen(MODAL_SCREENS.DEPOSIT)}
      title={translate('yield_confirm_offer_modal_title')}
      renderFooter={renderFooter}
      isScrollable>
      <div className="d-flex flex-row justify-content-between mt-4">
        <div className="d-flex flex-column">
          <p className="text-primary text-f4 text-bold">
            {`${formatNumber(
              details?.contractAttributes?.annualizedYieldPct,
              exchange.FIAT_VALUE
            )}%`}
          </p>
          <p className="text-shadow text-f6">
            {translate('yield_offer_details_modal_annualized_label')}
          </p>
        </div>
        <div className="d-flex flex-column align-items-end">
          <p className="text-dark text-f4 text-bold">
            {`${formatNumberWithComma(
              formatNumber(details?.contractAttributes?.amount, exchange.FIAT_VALUE)
            )} ${currencies.USDC.fullName}
            `}
          </p>
          <p className="text-shadow text-f6">
            {translate('yield_confrim_offer_modal_deposited_label')}
          </p>
        </div>
      </div>
      <div className="d-flex flex-row justify-content-between mt-4">
        <div className="d-flex flex-column align-items-start">
          <p className="text-dark text-f5 text-bold">
            {translate('yield_offer_details_modal_days_label').replace('__DAYS_NUMBER__', duration)}
          </p>
          <p className="text-shadow text-f6">
            {translate('yield_offer_details_modal_duration_label')}
          </p>
        </div>
        <div className="d-flex flex-column align-items-end">
          <p className="text-dark text-f5 text-bold">
            {details &&
              details?.product?.expirationDate &&
              moment(details?.product?.expirationDate, 'YYYYMMDDh:mm:ss').format('DD/MM/YYYY')}
          </p>
          <p className="text-shadow text-f6">
            {translate('yield_offer_details_modal_expiry_date_label')}
          </p>
        </div>
      </div>
      {!offerError && isLoadingOfferDetails && (
        <div>
          <GenericLoading isFullScreen={false} customSize="4rem" customSpacing="pt-4 mt-4" />
        </div>
      )}
      {!offerError && !isLoadingOfferDetails && (
        <>
          <p className="text-shadow text-f6 text-bold mt-4">
            {translate('yield_contirm_invest_received_amount_note')}
          </p>
          <div className="d-flex flex-column bg-light rounded-extra p-3 mt-2">
            <div className="d-flex flex-row justify-content-between">
              <div className="d-flex flex-row justify-content-start align-items-center">
                <div className="w-25 me-2">
                  <img
                    src={`./assets/icons/${currencies.USDC.fullName}.svg`}
                    className="w-100"
                    alt={currencies.USDC.fullName}
                  />
                </div>
                <p className="text-dark text-nowrap">{`${formatNumberWithComma(
                  formatNumber(details?.contractAttributes?.usdcPaidAmount, exchange.FIAT_VALUE)
                )} ${currencies.USDC.fullName}`}</p>
              </div>
              <p className="text-dark text-nowrap">{`${formatNumberWithComma(
                formatNumber(details?.contractAttributes?.usdcProfit, exchange.FIAT_VALUE)
              )} ${currencies.USDC.fullName}`}</p>
            </div>
            <div className="d-flex flex-row justify-content-between">
              <p className="text-shadow text-f6">
                {translate('yield_offer_details_modal_get_token_more_note').replace(
                  '__PRICE__',
                  formatNumberWithComma(
                    formatNumber(details?.product?.linkedPrice, exchange.FIAT_VALUE)
                  )
                )}
              </p>
              <p className="text-shadow text-f6">{translate('yield_offer_details_modal_profit')}</p>
            </div>

            <div className="d-flex flex-row justify-content-center align-items-center my-2">
              <hr className="m-0 text-shadow w-100" />
              <p className="text-shadow mx-2">{translate('yield_offer_details_modal_or')}</p>
              <hr className="m-0 text-shadow w-100" />
            </div>

            <div className="d-flex flex-row justify-content-between">
              <div className="d-flex flex-row justify-content-start align-items-center">
                <div className="w-25 me-2">
                  <img
                    src={`./assets/icons/${currencies.BTC.icon}.svg`}
                    className="w-100"
                    alt={currencies.USDC.fullName}
                  />
                </div>
                <p className="text-dark text-nowrap">{`${formatNumberWithComma(
                  formatNumber(details?.contractAttributes?.btcPaidAmount, currencies.BTC.decimal)
                )} BTC`}</p>
              </div>
              <p className="text-dark text-nowrap">{`${formatNumberWithComma(
                formatNumber(details?.contractAttributes?.btcBuyAt, exchange.FIAT_VALUE)
              )} ${currencies.USDC.fullName}`}</p>
            </div>
            <div className="d-flex flex-row justify-content-between">
              <p className="text-shadow text-f6">
                {translate('yield_offer_details_modal_get_token_less_note').replace(
                  '__PRICE__',
                  formatNumberWithComma(
                    formatNumber(details?.product?.linkedPrice, exchange.FIAT_VALUE)
                  )
                )}
              </p>
              <p className="text-shadow text-f6">
                {translate('yield_offer_details_modal_buy_at_label')}
              </p>
            </div>
          </div>
        </>
      )}
      {offerError && (
        <div className="d-flex flex-column justify-content-center align-items-center">
          <div className="text-center mt-5">
            <img className="w-100 mb-2" src="./assets/images/warning.png" alt="Success" />
          </div>
          <p className="text-dark text-f5 text-center text-bold mt-2">
            {translate('offer_error_msg')}
          </p>
        </div>
      )}
    </ModalBodyLayout>
  );
};

export default ConfirmDeposit;

ConfirmDeposit.propTypes = {
  offerDetails: PropTypes.object.isRequired,
  openScreen: PropTypes.func.isRequired,
  refetchDeposited: PropTypes.func.isRequired,
  setConfirmedOffer: PropTypes.func.isRequired,
  refrechWallets: PropTypes.func.isRequired,
  hide: PropTypes.func.isRequired,
  selectedYieldType: PropTypes.string.isRequired,
};
